import { Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { MaskInput, Notification, dateMaskTrack } from '@hero/atoms';
import { trackerManager } from '@hero/clients';
import {
  MAX_EMAIL_LENGTH,
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  TRACKER_EVENT
} from '@hero/constants';
import { TagSelect } from '@hero/molecules';
import { useBulkUpsertContact } from '@hero/services/contact-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { addNewContactSchema } from '@hero/validations';

import { STEP } from './constant';
import * as S from './style';

function NewContactForm({
  setFooterProps,
  onRequestClose,
  onUpdatingDone,
  setModalHeader,
  setActiveScene,
  selectedTag
}) {
  const { t } = useTranslation();
  const { mutateAsync: bulkUpsertContact, isSuccess } = useBulkUpsertContact();
  const { showSuccess } = useNotification();

  const sendNewContactsToApi = async contact => {
    const response = await bulkUpsertContact({ contacts: [contact] });

    trackerManager.emit(TRACKER_EVENT.contactAdded, {
      label: t('uploadSpreadsheet')
    });
    if (onUpdatingDone) onUpdatingDone(response);
  };

  const tags = [];
  if (selectedTag) {
    tags.push({
      label: selectedTag.name,
      value: selectedTag.id
    });
  }

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      birthday: '',
      anniversary: '',
      tags
    },
    validationSchema: addNewContactSchema,
    onSubmit: async data => {
      await sendNewContactsToApi(data);
      formik.resetForm();
      showSuccess({
        type: 'toast',
        message: t('contactAddedSuccessfully')
      });
    }
  });

  useEffect(() => {
    setModalHeader({
      title: t('individuallyAddContacts'),
      onBack: () => {
        setActiveScene(STEP.select);
      }
    });

    setFooterProps({
      leftButtonText: t('confirmAndAddNew'),
      leftButtonOnClick: formik.handleSubmit,
      isLeftButtonActive: !!formik.isValid,
      rightButtonText: t('confirmAndClose'),
      rightButtonOnClick: () => {
        formik.handleSubmit();
        onRequestClose();
      },
      isRightButtonActive: !!formik.isValid
    });
  }, [formik.isValid]);

  return (
    <S.NewContactForm onSubmit={formik.handleSubmit}>
      {formik.values.tags.filter(tag => tag?.automations?.length).length > 0 && (
        <Notification
          status="info"
          title={t('blueBadgeAutomation')}
          message={t('blueBadgeAutomationDesc')}
        />
      )}
      {isSuccess && <Notification status="success" title={t('contactAddedSuccessfully')} />}
      <Typography variant="body1Bold">{t('contactDetails')}:</Typography>
      <S.Content>
        <S.FormContainer>
          <S.Row>
            <S.Column>
              <Input
                name="firstName"
                label={`${t('firstName')} *`}
                variant="outlined"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={!!formik.errors.firstName}
                inputProps={{ maxLength: MAX_FIRST_NAME_LENGTH }}
                helperText={formik.errors.firstName}
              />
            </S.Column>

            <S.Column>
              <Input
                name="lastName"
                label={`${t('lastName')} *`}
                variant="outlined"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={!!formik.errors.lastName}
                inputProps={{ maxLength: MAX_LAST_NAME_LENGTH }}
                helperText={formik.errors.lastName}
              />
            </S.Column>
          </S.Row>
          <S.Column>
            <Input
              name="email"
              label={`${t('emailAddress')} *`}
              variant="outlined"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              error={!!formik.errors.email}
            />
          </S.Column>
          <S.Column>
            <MaskInput
              value={formik.values.birthday}
              onChange={e => {
                formik.setFieldValue('birthday', e.target.value);
              }}
              mask={'mm/dd/yyyy'}
              track={dateMaskTrack}
              placeholder={t('birthdayOptional')}
              error={!!formik.errors.birthday}
              helperText={formik.errors.birthday}
              replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
            />
          </S.Column>
          <S.Column>
            <MaskInput
              value={formik.values.anniversary}
              onChange={e => {
                formik.setFieldValue('anniversary', e.target.value);
              }}
              mask={'mm/dd/yyyy'}
              track={dateMaskTrack}
              placeholder={t('workAnniversaryOptional')}
              error={!!formik.errors.anniversary}
              helperText={formik.errors.anniversary}
              replacement={{ d: /\d/, m: /\d/, y: /\d/ }}
            />
          </S.Column>
          <Typography style={{ marginTop: 8 }} variant="body1Bold">
            {t('addTags')}:
          </Typography>
          <S.Column>
            <TagSelect
              value={formik.values.tags}
              onChange={tags => {
                formik.setFieldValue('tags', tags);
              }}
              optional
            />
            <S.InfoWrapper>
              <Icon name="info" color="var(--color-fg-weaker)" size="sm" />
              <Typography variant="caption1" emphasis="weaker">
                {t('addNewContactModalAddTagsInfo')}
              </Typography>
            </S.InfoWrapper>
          </S.Column>
        </S.FormContainer>
      </S.Content>
    </S.NewContactForm>
  );
}

export { NewContactForm };
